// Shared map of an overview: every selected corridor's centreline, the filtered
// deficiency sites and the multi-deficiency cluster rings. Same base maps, layer
// control, popups and focus protocol as the Safety Dashboard's map; no crash data.
// Receives ALREADY-FILTERED arrays. SVG renderer on purpose: Leaflet's canvas
// renderer in several panes lets only the top pane receive clicks.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer, TileLayer, LayersControl, LayerGroup, Pane, Polyline, CircleMarker, Popup, Tooltip, useMap, useMapEvents,
} from 'react-leaflet';
import StationsLayer, { DETAIL_ANALYSES, DETAIL_COLORS, GLR_BANDS, PATH_COLOR, SHAPES } from './StationsLayer';
import 'leaflet/dist/leaflet.css';
import { ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_COLORS, segmentName } from './overviewModel';
import { ManeuverButtons, ManeuverRows } from '../Safety/ManeuverLinks';

const fmtMp = (v) => (v == null ? '—' : Number(v).toFixed(2));

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => { if (bounds) map.fitBounds(bounds, { padding: [30, 30] }); }, [map, bounds]);
  return null;
}

function FlyToFocus({ focus, refs }) {
  const map = useMap();
  useEffect(() => {
    if (!focus || focus.lat == null) return undefined;
    const target = focus.id ? refs.current[focus.id] : null;
    const open = () => { try { if (target && target.openPopup) target.openPopup(); } catch (e) { /* layer gone */ } };
    map.flyTo([focus.lat, focus.lng], Math.max(map.getZoom(), focus.zoom || 15), { duration: 0.8 });
    map.once('moveend', open);
    return () => map.off('moveend', open);
  }, [map, focus, refs]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
}

const SHAPE_CLASS = { dot: 'lg-dot', circle: 'lg-dot', square: 'lg-dot ov-lg-square', diamond: 'lg-dot ov-lg-diamond' };
const Swatch = ({ color, label, shape = 'line' }) => (
  <div className="lg-row"><i className={SHAPE_CLASS[shape] || 'lg-line'} style={{ background: color }} />{label}</div>
);

/** What the single-analysis view draws, in that analysis' own report colours. */
function DetailLegend({ analysis }) {
  const shape = SHAPES[analysis] || 'dot';
  if (analysis === 'SSD' || analysis === 'PSD') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label={analysis === 'SSD' ? 'Station short of stopping sight distance' : 'No-pass station'} />
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Station passes" /></>);
  }
  if (analysis === 'GRW') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label="Guardrail warranted (YES)" />
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Not warranted (NO)" /></>);
  }
  if (analysis === 'GLR') return <>{GLR_BANDS.map(([label, color]) => <Swatch key={label} shape="dot" color={color} label={`${label} glare / yr`} />)}</>;
  if (analysis === 'FSA') {
    return (<><div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.flat }} />Flat spot, pointing downhill</div>
      <div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.slope }} />Station gradient direction</div></>);
  }
  if (analysis === 'ISD') {
    return (<><Swatch color={DETAIL_COLORS.bad} label="Sight ray: target not visible" />
      <Swatch color={DETAIL_COLORS.ok} label="Sight ray: target visible" /></>);
  }
  return Object.entries(SEVERITY_COLORS).map(([s, c]) => <Swatch key={s} color={c} label={`Curve, ${s}`} />);
}

function Legend({ colourBy, analyses, roads, details, showIntersections, showPaths }) {
  const detail = details.length > 0;
  const [open, setOpen] = React.useState(true);
  if (!open) {
    return <button type="button" className="safety-map-legend safety-legend-min" onClick={() => setOpen(true)}>Legend</button>;
  }
  return (
    <div className="safety-map-legend">
      <div className="lg-head">
        <span className="lg-title lg-title-first">{detail ? `${details[0]} · ${ANALYSIS_NAMES[details[0]]}` : colourBy === 'severity' ? 'Sites by severity' : 'Sites by analysis'}</span>
        <button type="button" className="lg-close" onClick={() => setOpen(false)} title="Minimize legend">–</button>
      </div>
      {detail ? (
        <>
          {details.map((a, i) => (
            <React.Fragment key={a}>
              {i > 0 && <div className="lg-title">{a} · {ANALYSIS_NAMES[a]}</div>}
              <DetailLegend analysis={a} />
            </React.Fragment>
          ))}
          {details.some((a) => DETAIL_ANALYSES.includes(a)) && (
            <>
              <div className="lg-title">Deficiency runs (halo, follows the severity filter)</div>
              {Object.entries(SEVERITY_COLORS).map(([s, c]) => (
                <div className="lg-row" key={s}><i className="lg-line ov-lg-halo" style={{ background: c }} />{s}</div>))}
            </>
          )}
          {showPaths && <Swatch color={PATH_COLOR} label="Analysis path" />}
        </>
      ) : colourBy === 'severity'
        ? Object.entries(SEVERITY_COLORS).map(([s, c]) => (
          <div className="lg-row" key={s}><i className="lg-line" style={{ background: c }} />{s}</div>))
        : analyses.map((a) => (
          <div className="lg-row" key={a} title={ANALYSIS_NAMES[a]}><i className="lg-line" style={{ background: ANALYSIS_COLORS[a] }} />{a}</div>))}
      {analyses.length > 1 && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#f8fafc', borderStyle: 'dashed' }} />Multi-deficiency cluster</div>}
      {showIntersections && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: ANALYSIS_COLORS.ISD }} />Intersection</div>}
      <div className="lg-title">Roads</div>
      {roads.map((r) => (
        <div className="lg-row" key={r.road}>
          <i className="lg-line" style={{ background: detail ? PATH_COLOR : r.color }} />
          {r.road}<span className="ov-legend-mp"> · {r.ranges.map(([a, b]) => `MP ${a.toFixed(1)}–${b.toFixed(1)}`).join(' · ')}</span>
        </div>
      ))}
    </div>
  );
}

const ReportButton = ({ url, label }) => (url ? (
  <a className="safety-btn safety-report-btn" href={url} target="_blank" rel="noreferrer">{label} ↗</a>
) : null);

export default function OverviewMap({ segments, sites, clusters, intersections = [], analyses, colourBy, roadColors, focus,
  details = [], stationLayers = null, showPaths = true, direction = '' }) {
  // details: the selected analyses when there are at most three (else []). Their stations are
  // drawn in each report's own colours on a light purple path; those analyses' deficiency runs
  // stay clickable but invisible, except HCA whose runs ARE the curves (severity colours).
  // Intersections are an ISD subject: shown only while ISD is among the selected analyses.
  const detail = details.length > 0;
  const withStations = new Set(details.filter((a) => DETAIL_ANALYSES.includes(a)));
  const showIntersections = analyses.includes('ISD');
  const refs = useRef({});
  const [zoom, setZoom] = useState(null);
  // Cluster rings are a close-up aid: they show only from zoom 13, where the colours under
  // them read clearly. Before the first zoom reading nothing is drawn (the first view of an
  // order is the whole order).
  const far = zoom == null || zoom < 13;
  const mid = zoom != null && zoom < 15;
  const stationsShown = withStations.size > 0;
  const runColourBy = colourBy;
  const colorOfRun = (s) => (detail && s.analysis === 'HCA' ? SEVERITY_COLORS[s.severity] : null);
  const bounds = useMemo(() => {
    const pts = segments.flatMap((s) => Object.values(s.directions || {}))
      .flatMap((d) => d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]]));
    return pts.length ? pts : null;
  }, [segments]);
  const segByKey = useMemo(() => new Map(segments.map((s) => [s.key, s])), [segments]);
  const roads = useMemo(() => {
    const m = new Map();
    segments.forEach((s) => {
      if (!m.has(s.road)) m.set(s.road, { road: s.road, color: roadColors[s.road], ranges: [] });
      if (s.mpMin != null) m.get(s.road).ranges.push([s.mpMin, s.mpMax]);
    });
    return Array.from(m.values()).map((r) => ({ ...r, ranges: r.ranges.sort((a, b) => a[0] - b[0]) }));
  }, [segments, roadColors]);
  const colorOf = (s) => colorOfRun(s) || (runColourBy === 'severity' ? SEVERITY_COLORS[s.severity] : ANALYSIS_COLORS[s.analysis]) || '#c92a2a';

  if (!bounds) return <div className="safety-map-empty">No surveyed centreline to show for this selection.</div>;

  return (
    <div className="safety-map-holder">
      <MapContainer bounds={bounds} className="safety-map" scrollWheelZoom>
        <FitBounds bounds={bounds} />
        <ZoomWatcher onZoom={setZoom} />
        <FlyToFocus focus={focus} refs={refs} />
        <Pane name="ovSites" style={{ zIndex: 410 }} />
        <Pane name="ovIntersections" style={{ zIndex: 420 }} />
        <Pane name="ovClusters" style={{ zIndex: 430 }} />
        <Pane name="ovDecor" style={{ zIndex: 470 }} />
        {focus && focus.lat != null && (
          <CircleMarker key={focus.stamp} center={[focus.lat, focus.lng]} radius={16} pane="ovDecor" interactive={false}
            pathOptions={{ color: '#ff9433', weight: 3, fillOpacity: 0.08, dashArray: '2 4' }} />
        )}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Street (OSM)">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite (Esri)">
            <TileLayer attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain (OpenTopoMap)">
            <TileLayer attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" maxZoom={17} />
          </LayersControl.BaseLayer>

          {showPaths && (
          <LayersControl.Overlay checked name="Analysis paths">
            <LayerGroup>
              {segments.flatMap((s) => Object.entries(s.directions || {}).filter(([dir]) => !direction || dir === direction).map(([dir, d]) => (
                <Polyline key={`${s.key}-${dir}`} positions={d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]])}
                  pathOptions={{ color: detail ? PATH_COLOR : (roadColors[s.road] || '#7aa7e8'), weight: detail ? 4 : 3, opacity: detail ? 0.85 : 0.6 }}>
                  <Tooltip sticky>{segmentName(s)} · {dir === 'P' ? 'positive' : 'negative'} direction · project #{s.projectId}</Tooltip>
                </Polyline>
              )))}
            </LayerGroup>
          </LayersControl.Overlay>
          )}

          {stationsShown && (
            <LayersControl.Overlay checked name={`${Array.from(withStations).join(' · ')} stations`}>
              <StationsLayer layers={stationLayers || []} />
            </LayersControl.Overlay>
          )}

          <LayersControl.Overlay checked name="Deficiency sites">
            <LayerGroup>
              {sites.map((s) => {
                const seg = segByKey.get(s.segKey);
                const popup = (
                  <Popup>
                    <div className="safety-popup">
                      <strong>{s.analysis} · {ANALYSIS_NAMES[s.analysis]}</strong>
                      <div className="safety-popup-mp">
                        {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)}{s.lengthMi ? `–${fmtMp(s.mpEnd)} · ${s.lengthMi.toFixed(2)} mi` : ''}
                      </div>
                      <table><tbody>
                        <tr><td>Direction</td><td>{s.direction === 'P' ? 'Positive' : 'Negative'}</td></tr>
                        <tr><td>Severity</td><td>{s.severity}</td></tr>
                        <tr><td>Worst</td><td>{s.worstText || '—'}</td></tr>
                        <tr><td>Project</td><td>#{s.projectId}</td></tr>
                        <ManeuverRows maneuvers={s.maneuvers} />
                      </tbody></table>
                      <div className="safety-popup-actions">
                        {/* ISD: one page per maneuver — link every maneuver that is short, worst first */}
                        <ManeuverButtons maneuvers={s.maneuvers} fallbackUrl={s.reportUrl}
                          fallbackLabel={`Open ${s.analysis} report at MP ${fmtMp(s.mpStart)}`} />
                      </div>
                    </div>
                  </Popup>
                );
                const tip = <Tooltip sticky>{s.analysis} · {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)} · {s.severity}</Tooltip>;
                const keep = (el) => { if (el) refs.current[s.id] = el; else delete refs.current[s.id]; };
                return s.coords && s.coords.length > 1 ? (
                  <Polyline key={s.id} ref={keep} positions={s.coords} pane="ovSites"
                    pathOptions={withStations.has(s.analysis)
                      // a HALO under the station marks, in the run's severity colour: it shows where the
                      // (severity-filtered) deficiency runs are and is the click target for their popup
                      ? { color: SEVERITY_COLORS[s.severity] || '#c92a2a', weight: far ? 7 : 13, opacity: 0.38 }
                      : { color: colorOf(s), weight: far ? 3 : 5, opacity: 0.95 }}>{tip}{popup}</Polyline>
                ) : (s.lat != null ? (
                  <CircleMarker key={s.id} ref={keep} center={[s.lat, s.lng]} radius={6} pane="ovSites"
                    pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: colorOf(s), fillOpacity: 0.95 }}>{tip}{popup}</CircleMarker>
                ) : null);
              })}
            </LayerGroup>
          </LayersControl.Overlay>

          {showIntersections && intersections.length > 0 && (
            <LayersControl.Overlay checked name="Intersections (ISD)">
              <LayerGroup>
                {intersections.filter((ix) => ix.lat != null).map((ix) => (
                  <CircleMarker key={ix.id} center={[ix.lat, ix.lng]} radius={5} pane="ovIntersections"
                    ref={(el) => { if (el) refs.current[ix.id] = el; else delete refs.current[ix.id]; }}
                    pathOptions={ix.severity
                      ? { color: '#ffffff', weight: 1.5, fillColor: SEVERITY_COLORS[ix.severity], fillOpacity: 0.95 }
                      : { color: ANALYSIS_COLORS.ISD, weight: 2, fillColor: '#0b1222', fillOpacity: 0.9 }}>
                    <Tooltip>Intersection {ix.name || ''} · MP {fmtMp(ix.mp)}{ix.severity ? ` · sight distance short (${ix.severity})` : ' · sight distance met'}</Tooltip>
                    <Popup>
                      <div className="safety-popup">
                        <strong>Intersection {ix.name || ''}</strong>
                        <div className="safety-popup-mp">{ix.segName} · MP {fmtMp(ix.mp)}</div>
                        <table><tbody>
                          {ix.isdFt ? <tr><td>Required sight distance</td><td>{Math.round(ix.isdFt)} ft</td></tr> : null}
                          {ix.quality != null ? <tr><td>Worst quality</td><td>{(ix.quality * 100).toFixed(0)}%</td></tr> : null}
                          <tr><td>Result</td><td>{ix.severity ? `short of required · ${ix.severity}` : 'meets the required distance'}</td></tr>
                          <ManeuverRows maneuvers={ix.maneuvers} />
                        </tbody></table>
                        <div className="safety-popup-actions">
                          <ManeuverButtons maneuvers={ix.maneuvers} fallbackUrl={ix.reportUrl}
                            fallbackLabel={`Open ISD report at MP ${fmtMp(ix.mp)}`} />
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          <LayersControl.Overlay checked name="Multi-deficiency clusters">
            <LayerGroup>
              {/* Rings are a close-up aid: zoomed out over a whole order they would bury the roads in white. */}
              {(far ? [] : clusters.filter((c) => c.lat != null)).map((c) => (
                <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={mid ? 8 : 12} pane="ovClusters"
                  ref={(el) => { if (el) refs.current[c.id] = el; else delete refs.current[c.id]; }}
                  pathOptions={{ color: '#f8fafc', weight: mid ? 1.4 : 2, fillOpacity: 0, dashArray: '4 3' }}>
                  <Tooltip>{c.n} analyses · {segmentName(c)} · MP {fmtMp(c.mpStart)}</Tooltip>
                  <Popup>
                    <div className="safety-popup">
                      <strong>{c.n} analyses at one place</strong>
                      <div className="safety-popup-mp">{segmentName(c)} · MP {fmtMp(c.mpStart)}–{fmtMp(c.mpEnd)}</div>
                      <table><tbody>
                        <tr><td>Analyses</td><td>{c.analyses.join(' · ')}</td></tr>
                        <tr><td>Sites</td><td>{c.sites}</td></tr>
                        <tr><td>Worst severity</td><td>{c.severity}</td></tr>
                      </tbody></table>
                      <div className="safety-popup-actions">
                        {Object.entries(c.reports).map(([a, site]) => (
                          <ReportButton key={a} url={site.reportUrl} label={`Open ${a} report at MP ${fmtMp(site.mpStart)}`} />
                        ))}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
      </MapContainer>
      {far && clusters.length > 0 && <div className="ov-map-hint">{clusters.length} multi-deficiency clusters · zoom in to see their rings</div>}
      <Legend colourBy={runColourBy} analyses={analyses} roads={roads} details={details} showIntersections={showIntersections && intersections.length > 0} showPaths={showPaths} />
    </div>
  );
}
